import React from "react";
import { Container, Group, Stack, Button, Grid, Box } from "@mantine/core";
import { IconArrowLeft, IconTools, IconArrowRight } from "@tabler/icons-react";
import { Head, Link } from "@inertiajs/react";
import AppShell from "../../Components/Layout/AppShell";
import SiteFooter from "../../Components/Layout/SiteFooter";
import { Card } from "../../Components/UI/Card";
import { Badge } from "../../Components/UI/Badge";
import { Heading1, Heading3, DimmedText } from "../../Components/UI/Typography";
import { ProjectCard } from "../../Components/ProjectCard";
import VideoEmbed from "../../Components/VideoEmbed";
import { getProjectGradient } from "../../src/utils/projectUtils";

interface ProjectMedia {
  id: number;
  media_type: "image" | "video" | "document" | "link";
  file_url: string;
  caption?: string;
  position: number;
}

interface Project {
  id: number;
  slug: string;
  title: string;
  description: string;
  client_name?: string;
  project_url?: string;
  project_type: string;
  featured_image_url: string;
  skills: string[];
  tools: string[];
  duration?: string;
  has_carousel: boolean;
  start_date?: string;
  end_date?: string;
  media?: ProjectMedia[];
}

interface Props {
  project: Project;
  related_projects: Project[];
}

const BuildShow: React.FC<Props> = ({ project, related_projects }) => {
  return (
    <AppShell>
      <Head>
        <title>{`${project.title} | Ogarocious`}</title>
        <meta name="description" content={project.description.slice(0, 155)} />
        <meta property="og:title" content={`${project.title} | Ogarocious`} />
        <meta property="og:description" content={project.description.slice(0, 155)} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL || ""}/tech/${project.slug}`} />
        <meta property="og:image" content={project.featured_image_url || import.meta.env.VITE_OG_IMAGE_URL || ""} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${project.title} | Ogarocious`} />
        <meta name="twitter:description" content={project.description.slice(0, 155)} />
        <meta name="twitter:image" content={project.featured_image_url || import.meta.env.VITE_OG_IMAGE_URL || ""} />
      </Head>
      <Box
        p="0px"
        style={{
          background:
            "linear-gradient(180deg, #0a0705 0%, #0f0b08 50%, #0a0705 100%)",
          minHeight: "100vh",
        }}
      >
        <Container size="lg" py={80}>
          <Stack gap={40}>
            {/* Navigation */}
            <Group>
              <Button
                variant="subtle"
                color="gray"
                leftSection={<IconArrowLeft size={16} />}
                component={Link}
                href="/tech"
                style={{ color: "rgba(161, 161, 170, 0.8)" }}
              >
                Back to Build
              </Button>
            </Group>

            {/* Project Header */}
            <Box>
              <Group justify="space-between" align="flex-start" mb="lg">
                <Stack gap="sm" style={{ flex: 1 }}>
                  <Badge
                    size="lg"
                    style={{
                      background: getProjectGradient(project.project_type),
                      color: "white",
                      border: "none",
                      fontWeight: 600,
                      textTransform: "capitalize",
                    }}
                  >
                    {project.project_type}
                  </Badge>

                  <Heading1 style={{ lineHeight: 1.2 }}>
                    {project.title}
                  </Heading1>
                </Stack>
              </Group>

              <DimmedText size="md" style={{ lineHeight: 1.6 }}>
                {project.description}
              </DimmedText>
            </Box>

            {/* Video Section */}
            {project.project_url && (
              <VideoEmbed url={project.project_url} title={project.title} />
            )}

            {/* Project Details Grid */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 12, md: 6, lg: 8, xl: 8 }}>
                <Stack gap="sm">
                  <Card radius="md" p="md">
                    <Stack gap="lg">
                      <Group gap="xs">
                        <IconTools size={24} style={{ color: "rgba(161, 161, 170, 0.6)" }} />
                        <Heading3>Skills &amp; Tools</Heading3>
                      </Group>

                      <Stack gap="md">
                        <Box>
                          <DimmedText size="sm" mb="xs">Skills Used</DimmedText>
                          <Group gap="xs">
                            {project.skills.map((skill, index) => (
                              <Badge key={index} size="md" radius="md" variant="skills">
                                {skill}
                              </Badge>
                            ))}
                          </Group>
                        </Box>

                        <Box>
                          <DimmedText size="sm" mb="xs">Tools &amp; Software</DimmedText>
                          <Group gap="xs">
                            {project.tools.map((tool, index) => (
                              <Badge key={index} size="md" radius="md" variant="tools">
                                {tool}
                              </Badge>
                            ))}
                          </Group>
                        </Box>
                      </Stack>
                    </Stack>
                  </Card>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                {related_projects.length > 0 && (
                  <Card radius="md" p="md">
                    <Stack gap="lg">
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconTools size={24} style={{ color: "rgba(161, 161, 170, 0.6)" }} />
                          <Heading3>Similar Projects</Heading3>
                        </Group>
                        <Button
                          variant="subtle"
                          size="sm"
                          color="teal"
                          rightSection={<IconArrowRight size={14} />}
                          component="a"
                          href="/tech"
                        >
                          View All
                        </Button>
                      </Group>

                      <Grid>
                        {related_projects.slice(0, 3).map((relatedProject) => (
                          <Grid.Col
                            key={relatedProject.id}
                            span={{ base: 12, sm: 6, md: 12 }}
                          >
                            <ProjectCard
                              project={relatedProject}
                              variant="compact"
                            />
                          </Grid.Col>
                        ))}
                      </Grid>
                    </Stack>
                  </Card>
                )}
              </Grid.Col>
            </Grid>
          </Stack>
        </Container>
        <SiteFooter />
      </Box>
    </AppShell>
  );
};

export default BuildShow;
